import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import posthog from 'posthog-js';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './queryClient';
import { TooltipProvider } from '@medusajs/ui';
import { AuthProvider } from './contexts/AuthContext';
import { BusinessProfileProvider } from './contexts/BusinessProfileContext';
import { Toaster } from './components/Toast/Toaster';
import { PWAUpdatePrompt } from './components/PWA/PWAUpdatePrompt';
import { BannerProvider } from './contexts/BannerContext';
import { LocationProvider } from './contexts/LocationContext';
import { SocketProvider } from './contexts/WebSocketContext';

import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import LoginPage from './components/Auth/LoginPage';
import RegisterPage from './components/Auth/RegisterPage';
import ForgotPasswordPage from './components/Auth/ForgotPasswordPage';
import ResetPasswordPage from './components/Auth/ResetPasswordPage';
import ScrollToTop from './components/ScrollToTop';
import { EntitlementsProvider } from '@/contexts/EntitlementsContext';
import FeatureGate from '@/components/Billing/FeatureGate';
import GlobalPlanUpgradeModal from '@/components/Billing/GlobalPlanUpgradeModal';

const DashboardPage = lazy(() => import('@/components/Dashboard/DashboardPage'));
const ProductCreatePage = lazy(() => import('@/components/Inventory/Products/ProductCreatePage'));
const CreateLocationPage = lazy(() => import('@/components/Locations/CreateLocationPage'));
const CustomerCreatePage = lazy(() => import('@/components/Customers/CustomerCreatePage'));
const CustomerEditPage = lazy(() => import('@/components/Customers/CustomerEditPage'));

const DataImportPage = lazy(() => import('@/components/DataImport/DataImportPage'));
const ProductsPage = lazy(() => import('@/components/Inventory/Products/ProductsPage'));
const PriceHistoryPage = lazy(() => import('@/components/Inventory/PriceHistory/PriceHistoryPage'));
const SettingsPage = lazy(() => import('@/components/Settings/SettingsPage'));
const BillingPage = lazy(() => import('@/components/Billing/BillingPage'));
const MyProfilePage = lazy(() => import('@/components/Profile/MyProfilePage'));
const StaffPage = lazy(() => import('@/components/Staff/StaffPage'));
const ExpensesPage = lazy(() => import('@/components/Expenses/ExpensesPage'));
const SuppliersPage = lazy(() => import('@/components/Suppliers/SuppliersPage'));
const StaffPortalPage = lazy(() => import('@/components/Workforce/StaffPortalPage'));
const TrainingHub = lazy(() => import('@/components/Training/TrainingHub'));
const CourseBuilder = lazy(() => import('@/components/Training/CourseBuilder'));
const LessonPlayerPage = lazy(() => import('@/components/Training/LessonPlayerPage'));
const CourseOverviewPage = lazy(() => import('@/components/Training/CourseOverviewPage'));
const POSScreen = lazy(() => import('@/components/POS/POSScreen'));
const CustomerDisplayPage = lazy(() => import('@/components/POS/CustomerDisplay/CustomerDisplayPage'));

const PrivacyPage = lazy(() => import('@/components/Legal/PrivacyPage'));
const TermsPage = lazy(() => import('@/components/Legal/TermsPage'));
const OnlineStorePage = lazy(() => import('@/components/OnlineStore/OnlineStorePage'));
const LocationsPage = lazy(() => import('@/components/Locations/LocationsPage'));
const OrdersPage = lazy(() => import('@/components/Orders/OrdersPage'));
const OrderDetailsPage = lazy(() => import('@/components/Orders/OrderDetailsPage'));
const CustomersPage = lazy(() => import('@/components/Customers/CustomersPage'));
const CustomerDetailsPage = lazy(() => import('@/components/Customers/CustomerDetailsPage'));
const ReportsPage = lazy(() => import('@/components/Reports/ReportsPage'));
const AccountingPage = lazy(() => import('@/components/Accounting/AccountingPage'));
const VatStatementPage = lazy(() => import('@/components/Accounting/VatStatementPage'));


import { Loading } from '@/components/Loading';

function PostHogPageView() {
  const location = useLocation();
  useEffect(() => {
    if (import.meta.env.VITE_PUBLIC_POSTHOG_KEY) {
      posthog.capture('$pageview');
    }
  }, [location]);
  return null;
}

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50/50 dark:bg-black">
    <Loading />
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
        
          <BannerProvider>
            <Router>
              <ScrollToTop />
              <PostHogPageView />
              <AuthProvider>
                <BusinessProfileProvider>
                  <LocationProvider>
                    <EntitlementsProvider>
                      <SocketProvider>
                      <div className="App lg:flex lg:flex-col lg:min-h-screen">
                      <Toaster />
                      <PWAUpdatePrompt />
                      <Suspense fallback={<PageLoader />}>
                        <Routes>
                          <Route path="/login" element={<LoginPage />} />
                          <Route path="/register" element={<RegisterPage />} />
                          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                          <Route path="/reset-password" element={<ResetPasswordPage />} />
                          <Route path="/privacy" element={<PrivacyPage />} />
                          <Route path="/terms" element={<TermsPage />} />
                          <Route
                            path="/getting-started"
                            element={<Navigate to="/home" replace />}
                          />
                          <Route
                            path="/home"
                            element={
                              <ProtectedRoute>
                                <DashboardPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/dashboard"
                            element={<Navigate to="/home" replace />}
                          />
                          <Route
                            path="/dashboard-legacy"
                            element={<Navigate to="/home" replace />}
                          />

                          <Route
                            path="/inventory/price-history"
                            element={
                              <ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'STOCK_MANAGER']}>
                                <PriceHistoryPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/products/*"
                            element={
                              <ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'STOCK_MANAGER']}>
                                <ProductsPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/products/new"
                            element={
                              <ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'STOCK_MANAGER']}>
                                <ProductCreatePage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/import"
                            element={
                              <ProtectedRoute allowedRoles={['OWNER', 'MANAGER']}>
                                <DataImportPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/sales"
                            element={<Navigate to="/orders" replace />}
                          />
                          <Route
                            path="/sales/:id"
                            element={<Navigate to="/orders" replace />}
                          />
                          <Route
                            path="/orders"
                            element={
                              <ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'CASHIER']}>
                                <OrdersPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/orders/:id"
                            element={
                              <ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'CASHIER']}>
                                <OrderDetailsPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/customers"
                            element={
                              <ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'CASHIER']}>
                                <CustomersPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/customers/new"
                            element={
                              <ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'CASHIER']}>
                                <CustomerCreatePage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/customers/edit/:id"
                            element={
                              <ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'CASHIER']}>
                                <CustomerEditPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/customers/:id"
                            element={
                              <ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'CASHIER']}>
                                <CustomerDetailsPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/reports/*"
                            element={
                              <ProtectedRoute allowedRoles={['OWNER', 'MANAGER']}>
                                <ReportsPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route path="/vat-returns" element={<Navigate to="/vatreturn" replace />} />
                          <Route path="/vat-returns/:id" element={<Navigate to="/vatreturn/:id" replace />} />
                          <Route path="/taxes" element={<Navigate to="/vatreturn" replace />} />
                          <Route path="/taxes/:id" element={<Navigate to="/vatreturn/:id" replace />} />
                          <Route
                            path="/vatreturn"
                            element={
                              <ProtectedRoute allowedRoles={['OWNER', 'MANAGER']}>
                                <FeatureGate feature="vatReturns">
                                  <AccountingPage />
                                </FeatureGate>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/vatreturn/:id"
                            element={
                              <ProtectedRoute allowedRoles={['OWNER', 'MANAGER']}>
                                <FeatureGate feature="vatReturns">
                                  <VatStatementPage />
                                </FeatureGate>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/staff/*"
                            element={
                              <ProtectedRoute allowedRoles={['OWNER', 'MANAGER']}>
                                <StaffPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/time-tracking"
                            element={<Navigate to="/staff?tab=time-tracking" replace />}
                          />
                          <Route
                            path="/shifts"
                            element={<Navigate to="/staff?tab=shifts" replace />}
                          />
                          <Route
                            path="/workforce"
                            element={<Navigate to="/staff?tab=holidays" replace />}
                          />
                          <Route
                            path="/settings/my-profile"
                            element={
                              <ProtectedRoute>
                                <MyProfilePage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/settings/locations/new"
                            element={
                              <ProtectedRoute allowedRoles={['OWNER']}>
                                <CreateLocationPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/settings/locations/:id/edit"
                            element={
                              <ProtectedRoute allowedRoles={['OWNER']}>
                                <CreateLocationPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/locations"
                            element={<Navigate to="/settings/locations" replace />}
                          />
                          <Route
                            path="/settings/locations"
                            element={
                              <ProtectedRoute allowedRoles={['OWNER']}>
                                <LocationsPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/settings/*"
                            element={
                              <ProtectedRoute allowedRoles={['OWNER']}>
                                <SettingsPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/billing/*"
                            element={
                              <ProtectedRoute allowedRoles={['OWNER']}>
                                <BillingPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/online-store/*"
                            element={
                              <ProtectedRoute allowedRoles={['OWNER', 'MANAGER']}>
                                <FeatureGate feature="onlineStore">
                                  <OnlineStorePage />
                                </FeatureGate>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/training/courses/new"
                            element={
                              <ProtectedRoute allowedRoles={['OWNER', 'MANAGER']}>
                                <FeatureGate feature="training">
                                  <CourseBuilder />
                                </FeatureGate>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/training/courses/:id/edit"
                            element={
                              <ProtectedRoute allowedRoles={['OWNER', 'MANAGER']}>
                                <FeatureGate feature="training">
                                  <CourseBuilder />
                                </FeatureGate>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/training/courses/:id"
                            element={
                              <ProtectedRoute>
                                <FeatureGate feature="training">
                                  <CourseOverviewPage />
                                </FeatureGate>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/training/courses/:id/learn"
                            element={
                              <ProtectedRoute>
                                <FeatureGate feature="training">
                                  <LessonPlayerPage />
                                </FeatureGate>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/training"
                            element={
                              <ProtectedRoute>
                                <FeatureGate feature="training">
                                  <TrainingHub />
                                </FeatureGate>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/my-portal"
                            element={
                              <ProtectedRoute>
                                <StaffPortalPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/expenses/*"
                            element={
                              <ProtectedRoute allowedRoles={['OWNER', 'MANAGER']}>
                                <ExpensesPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/suppliers/*"
                            element={
                              <ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'STOCK_MANAGER']}>
                                <SuppliersPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/pos"
                            element={
                              <ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'CASHIER']}>
                                <POSScreen />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/pos-new"
                            element={<Navigate to="/pos" replace />}
                          />
                          <Route
                            path="/customer-display"
                            element={
                              <ProtectedRoute allowedRoles={['OWNER', 'MANAGER', 'CASHIER']}>
                                <CustomerDisplayPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route path="/" element={<Navigate to="/home" replace />} />
                          <Route path="*" element={<Navigate to="/home" replace />} />
                        </Routes>
                      </Suspense>
                      </div>
                      </SocketProvider>
                      <GlobalPlanUpgradeModal />
                    </EntitlementsProvider>
                  </LocationProvider>
                </BusinessProfileProvider>
              </AuthProvider>
            </Router>
          </BannerProvider>
        
      </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;




